import type { QuizBuilderSectionSchema, QuizBuilderSectionsSchema } from '@repo/schemas/quiz/quizBuilder';
import type { QuizBuilderQuestionSchema } from '@repo/schemas/quiz/quizBuilderQuestion';
import type { ComponentProps } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { CollisionPriority } from '@dnd-kit/abstract';
import { arrayMove } from '@dnd-kit/helpers';
import { DragDropProvider, DragOverlay } from '@dnd-kit/react';
import { isSortable } from '@dnd-kit/react/sortable';
import { cn } from '@repo/lib/cn';
import { GripVerticalIcon } from 'lucide-react';
import { useState } from 'react';

import QuestionBuilderCard from '@/components/features/quiz/question/cards/QuestionBuilderCard';
import QuizBuilderQuestionDialog from '@/components/features/quiz/quiz-builder/QuizBuilderQuestionDialog';
import QuizBuilderQuestionSeparator from '@/components/features/quiz/quiz-builder/QuizBuilderQuestionSeparator';
import QuizBuilderSectionCardHeader from '@/components/features/quiz/quiz-builder/QuizBuilderSectionCardHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DroppableArea, SortableItem } from '@/components/ui/dnd';
import { questionsLabel } from '@/lib/questions-labels';

interface QuizBuilderSectionCardProps extends ComponentProps<'div'> {
  section: QuizBuilderSectionSchema;
  sectionIndex: number;
  removeSection: (index: number) => void;
  appendQuestion: (defaultValues: QuizBuilderQuestionSchema, index: number, insertIndex?: number) => void;
  removeQuestion: (sectionIndex: number, questionIndex: number) => void;
  duplicateQuestion: (sectionIndex: number, questionIndex: number) => void;
  duplicateSection: (sectionIndex: number) => void;
  sectionsForm: UseFormReturn<QuizBuilderSectionsSchema>;
}

export default function QuizBuilderSectionCard(quizBuilderSectionCardProps: QuizBuilderSectionCardProps) {
  const { section, sectionIndex, removeSection, appendQuestion, removeQuestion, duplicateQuestion, duplicateSection, className, sectionsForm, ...props } = quizBuilderSectionCardProps;

  const [open, setOpen] = useState<boolean>(false);
  const [insertIndex, setInsertIndex] = useState<number | undefined>(undefined);

  return (
    <div data-section-index={sectionIndex} className={cn('bg-accent border-border-secondary flex flex-col gap-2 rounded-lg p-1', className)} {...props}>
      <QuizBuilderQuestionDialog
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) {
            // Reset insert index when dialog closes
            setInsertIndex(undefined);
          }
        }}
        onSelect={(defaultValues) => appendQuestion(defaultValues, sectionIndex, insertIndex)}
      />
      <QuizBuilderSectionCardHeader section={section} sectionIndex={sectionIndex} onDelete={removeSection} onDuplicate={duplicateSection} />
      <div className="flex flex-col gap-1">
        <DragDropProvider
          onDragEnd={(event) => {
            const { target, source } = event.operation;
            const { canceled } = event;

            if (canceled) return;
            if (!target || !source) return;
            if (!isSortable(source)) return;

            const oldIndex: number = source.data.index;
            const newIndex: number = source.sortable.index;

            const reordered = arrayMove(section.questions, oldIndex, newIndex);
            sectionsForm.setValue(`content.${sectionIndex}.questions`, reordered);
          }}
        >
          {section.questions.length === 0 && (
            <DroppableArea
              droppableProps={{
                id: 'question-box',
                collisionPriority: CollisionPriority.Low,
                accept: 'question',
                data: { sectionIndex, index: 0 },
              }}
              className="flex flex-col gap-2"
            >
              <div className="text-muted-foreground bg-background/50 border-border-destructive/50 rounded-md border border-dashed px-3 py-6 text-center text-sm">
                <span>Brak pytań. Dodaj nowe pytanie.</span>
              </div>
            </DroppableArea>
          )}

          {section.questions.map((question, questionIndex) => (
            <SortableItem
              key={question.id}
              id={question.id}
              index={questionIndex}
              data={{
                index: questionIndex,
                sectionIndex: sectionIndex,
                question,
              }}
              args={{
                type: 'question',
                accept: 'question',
                group: sectionIndex,
              }}
            >
              <div className="flex flex-col gap-1">
                <QuizBuilderQuestionSeparator
                  onClick={() => {
                    setInsertIndex(questionIndex);
                    setOpen(true);
                  }}
                />
                <QuestionBuilderCard
                  sectionIndex={sectionIndex}
                  questionIndex={questionIndex}
                  questionType={question.type}
                  removeQuestion={() => removeQuestion(sectionIndex, questionIndex)}
                  duplicateQuestion={() => duplicateQuestion(sectionIndex, questionIndex)}
                />
              </div>
            </SortableItem>
          ))}
          <DragOverlay>
            {(source) => {
              if (source?.type !== 'question' || !source?.data) {
                return null;
              }
              const questionData = source.data.question as QuizBuilderQuestionSchema | undefined;
              const questionIndex = source.data.index as number | undefined;

              if (!questionData || questionIndex === undefined) return null;

              const questionType = questionData.type;
              const label = questionsLabel[questionType]?.label || questionType;
              const IconComponent = questionsLabel[questionType]?.icon;

              return (
                <div className="border-border-secondary bg-background pointer-events-none flex items-center gap-2 rounded-lg border p-2 shadow-lg">
                  <GripVerticalIcon className="text-text-secondary size-4 shrink-0" />
                  <Badge variant={'muted'} size={'sm'} className="min-w-6 shrink-0 justify-center p-0.5 text-xs">
                    {String(questionIndex + 1).padStart(2, '0')}
                  </Badge>
                  <Badge variant={'muted'} size={'sm'}>
                    {IconComponent && <IconComponent />}
                    {label}
                  </Badge>
                </div>
              );
            }}
          </DragOverlay>
        </DragDropProvider>
        <Button
          variant="outline"
          size="sm"
          className="mt-2 w-full border-dashed"
          onClick={() => {
            setInsertIndex(undefined);
            setOpen(true);
          }}
        >
          Dodaj pytanie
        </Button>
      </div>
    </div>
  );
}
